/// <amd-module name="Wit/QueriesHub/Common/Components/QueryHeader" />

import * as React from "react";
import { getTFSPageData } from "Tfs/Platform/Page";
import {
    IObservableArray,
    IObservableArrayEventArgs,
    IObservableValue,
    IReadonlyObservableArray,
    IReadyableObservableArray,
    ObservableArray,
    ObservableArrayAction,
    ObservableValue
} from "VSS/Core/Observable";
import { ScreenSize } from "VSS/Core/Util/Screen";
import { format } from "VSS/Core/Util/String";
import { getMenuItems } from "VSS/Features/PlatformUI/ContributedMenu";
import { getTabs } from "VSS/Features/PlatformUI/ContributedTab";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { onClickFPS } from "VSS/Platform/FPS";
import { IVssHistoryService } from "VSS/Platform/History";
import { IVssKeyboardShortcutService } from "VSS/Platform/KeyboardShortcuts";
import { IVssComponentContext, IVssLayoutManager, VssComponent } from "VSS/Platform/Layout";
import { Button } from "VSSUI/Button";
import { ButtonGroup } from "VSSUI/ButtonGroup";
import { Dropdown, DropdownExpandableButton } from "VSSUI/Dropdown";
import { HeaderCommandBar, IHeaderCommandBarItem } from "VSSUI/HeaderCommandBar";
import { IListSelection, ListSelection } from "VSSUI/List";
import { IListBoxItem } from "VSSUI/ListBox";
import { IMenuItem, IMenuItemDetails, MenuCell } from "VSSUI/Menu";
import { Observer } from "VSSUI/Observer";
import { IVssContributedTab, Tab, TabBar, TabProvider, TabSize } from "VSSUI/Tabs";
import { css } from "VSSUI/Util";
import { DropdownSelection } from "VSSUI/Utilities/DropdownSelection";
import { ScreenSizeObserver } from "VSSUI/Utilities/ScreenSize";
import { ISelectionRange } from "VSSUI/Utilities/Selection";
import { QueryType } from "Wit/Clients/Wit/Contracts/WorkItemTracking";
import { FeatureFlags } from "Wit/Common/Constants/FeatureFlags";
import { CoreFieldRefNames } from "Wit/Common/Generated/Constants";
import { IHotKeyEventType, IHotKeyEventService } from "Wit/Common/HotKeyEventService/IHotKeyEventService";
import { IQueryListService } from "Wit/Common/QueryListServices/IQueryListService";
import { QuerySaveDialog } from "Wit/Common/QuerySaveDialog/Components/QuerySaveDialog";
import { IQueryService, QueryDataProviders, QueryHubView } from "Wit/Common/QueryService/IQueryService";
import { reportError } from "Wit/Common/QueryUtils/ErrorHandlingUtils";
import { QueryItem, QuerySaveDialogMode } from "Wit/Common/QueryUtils/Models";
import { getEditorUrl } from "Wit/Common/QueryUtils/RoutingUtils";
import * as WorkItemUtils from "Wit/Common/Utils/WorkItemUtils/CommonWorkItemActions";
import { EmailWorkItemsDialog } from "Wit/Common/WorkItemDialogs/Components/EmailWorkItemsDialog";
import { WorkItemFormDialog } from "Wit/Common/WorkItemDialogs/Components/WorkItemFormDialog";
import { IWorkItemTypesService } from "Wit/Common/WorkItemTypesService/IWorkItemTypesService";
import { ColumnOptionsPanelContainer } from "Wit/QueriesHub/Common/Components/ColumnOptionsPanelContainer";
import { RevertDialog } from "Wit/QueriesHub/Common/Components/RevertDialog";
import { QueryUtils } from "Wit/QueriesHub/Common/Utils/QueryUtils";
import * as Resources from "Wit/QueriesHub/Resources";
import { QueryResultCSVExporter } from "Wit/QueriesHub/ResultsView/QueryResultCSVExporter";
import { WorkItemTypeIconProvider } from "Wit/UI/ComponentProviders";
import { WorkItemTrackingDataProvider } from "Wit/UI/DataProviders";
import { WorkItemTypeIcon } from "Wit/UI/WorkItemTypeIcon";
import { IDataProviderResponse } from "Wit/WorkItemDataManager/Contracts";
import {
    IWorkItem,
    IWorkItemBulkSaveResult,
    IWorkItemPermissionData,
    IWorkItemSavingFailedError,
    IWorkItemsBulkSaveSuccessResult
} from "Wit/WorkItemModel/OM/WorkItemInterfaces";
import { IWorkItemSavingService } from "Wit/WorkItemModel/Services/WorkItemSavingService";
import { IQueryContext, IQueryResultItem, QueryTabId } from "../Interfaces";
import { CopyUrlDialog } from "./CopyUrlDialog";
import { QueryBreadcrumb } from "./QueryBreadcrumb";
import { SaveWorkItemsDialog } from "./SaveWorkItemsDialog";

export const enum SplitterOptions {
    Bottom = "Bottom",
    Right = "Right",
    Off = "Off"
}

export interface IQueryHeaderProps {
    hideFocusArrows?: boolean;
    isDirty?: IObservableValue<boolean>;
    isImport?: boolean;
    isImportPanelOpen?: ObservableValue<boolean>;
    lastFocusedRow?: ObservableValue<number>;
    onToggleFilterBar?: () => void;
    onFocusNext?: () => void;
    onFocusPrevious?: () => void;
    onAddChart?: () => void;
    onOpenImportPanel?: () => void;
    onRenameQuery?: () => void;
    onRevert?: () => void;
    onRunQuery?: () => void;
    onSplitterDirectionSelect?: (value: string) => void;
    onSaveItemsComplete?: (workItems: IWorkItem[]) => void;
    queryResultItems?: IReadyableObservableArray<IQueryResultItem>;
    selection?: ListSelection;
    selectedTabId?: string;
    splitterDirectionIndex?: number;
    workItemTrackingDataProvider?: WorkItemTrackingDataProvider;
    isFiltering?: boolean;
    targetIdsCount?: number;
    queryType?: QueryType;
}

interface IContributedActionContext {
    query?: IQueryContext;
    queryText: string;
    workItemIds: string[];
}

export class QueryHeader extends VssComponent<IQueryHeaderProps> {
    private layoutManager: IVssLayoutManager;
    private splitterDirectionSelection: DropdownSelection;
    private queryService: IQueryService;
    private saveDialogMode = new ObservableValue<QuerySaveDialogMode>(QuerySaveDialogMode.NewQuery);
    private workItemResults = new ObservableArray<IWorkItemBulkSaveResult>();
    private contributedActions: IReadonlyObservableArray<IMenuItem>;
    private contributedTabs: IReadonlyObservableArray<IVssContributedTab>;
    private actionContext: IContributedActionContext = { query: undefined, queryText: "", workItemIds: [] };
    private projectId: string = "";
    private shortcutService: IVssKeyboardShortcutService;
    private areWorkItemsDirty = new ObservableValue<boolean>(false);
    private selectedWorkItemIds = new ObservableArray<string>([]);
    private workItemPermissions = new ObservableValue<IWorkItemPermissionData | undefined>(undefined);
    private responsiveLayoutFlickeringFixEnabled: boolean;
    private hasBacklogManagementPermission: boolean;
    private enableNewBoardsQueriesNewDesignFF: boolean;

    constructor(props: IQueryHeaderProps, context: IVssComponentContext) {
        super(props, context);

        this.hasBacklogManagementPermission = this.hasAgileToolsBacklogManagementPermission();
        this.responsiveLayoutFlickeringFixEnabled = isFeatureFlagEnabled(this.context.pageContext, FeatureFlags.ResponsiveLayoutFlickeringFixEnabled, false);
        this.projectId = getTFSPageData(this.context.pageContext)?.project?.id ?? "";
        this.layoutManager = context.pageContext.getService<IVssLayoutManager>("IVssLayoutManager");
        this.splitterDirectionSelection = new DropdownSelection();
        this.splitterDirectionSelection.select(props.splitterDirectionIndex ?? 1); // Select "Right" by default if we don't have a setting saved.

        this.shortcutService = this.context.pageContext.getService<IVssKeyboardShortcutService>("IVssKeyboardShortcutService");
        this.queryService = this.context.pageContext.getService<IQueryService>("IQueryService");
        this.contributedActions = getMenuItems(
            this.context.pageContext,
            "ms.vss-work-web.work-item-query-results-toolbar-menu",
            this.actionContext,
            undefined,
            true
        );
        if (this.queryService.isRecycleBin) {
            this.contributedActions = getMenuItems(
                this.context.pageContext,
                "ms.vss-work-web.recycle-bin-context-menu",
                this.actionContext,
                undefined,
                true
            );
        }
        this.contributedTabs = getTabs(this.context.pageContext, "ms.vss-work-web.query-tabs", { contextualData: this.actionContext });
        this._getContributedItemContext();
        this.enableNewBoardsQueriesNewDesignFF = isFeatureFlagEnabled(this.context.pageContext, FeatureFlags.EnableNewBoardsQueriesNewDesign);
    }

    public async componentDidMount() {
        if (this.props.selection) {
            this.props.selection.subscribe(this._onSelectionUpdated);
        }
        if (this.props.queryResultItems) {
            this.props.queryResultItems.subscribe(this._onSelectionUpdated);
        }
        if (this.context.pageContext) {
            const contributionService = this.context.pageContext.getService<IVssContributionService>("IVssContributionService");
            this.workItemPermissions.value = await contributionService.getDataAsync<IWorkItemPermissionData>(
                QueryDataProviders.WorkItemPermissionsDataProvider
            );
        }
        this.registerShortcuts();
        this.subscribeToWorkItemChanges();
        window.addEventListener("beforeunload", this.onBeforeUnload);
    }

    private hasAgileToolsBacklogManagementPermission(): boolean {
        const pageDataService = this.context.pageContext.getService<IVssContributionService>("IVssContributionService");
        const permissionData = pageDataService.getData<{ hasPermission: boolean }>("ms.vss-work-web.backlog-permission-data-provider");
        return !!permissionData && permissionData.hasPermission;
    }

    private getLinksStatusString(): string | undefined {
        if (
            !this.props.queryResultItems?.length ||
            this.props.queryResultItems.length <= 0 ||
            this.props.isFiltering ||
            !(this.queryService.queryResult.value?.data?.isTreeQuery || this.queryService.queryResult.value?.data?.isLinkQuery)
        ) {
            return;
        }

        const linkedItemsCount = this.queryService.queryResult.value.data.sourceIds.filter(id => id > 0).length;
        const topLevelItemsCount = this.getItemsCount(this.queryService.queryResultItems) - linkedItemsCount;

        return format(Resources.QueryResultsTreeStatusTextFormat, topLevelItemsCount, linkedItemsCount);
    }

    public componentWillUnmount() {
        if (this.props.selection) {
            this.props.selection.unsubscribe(this._onSelectionUpdated);
        }
        if (this.props.queryResultItems) {
            this.props.queryResultItems.unsubscribe(this._onSelectionUpdated);
        }
        this.unregisterShortcuts();
        this.unsubscribeFromWorkItemChanges();
        window.removeEventListener("beforeunload", this.onBeforeUnload);
    }

    private onBeforeUnload = (e: BeforeUnloadEvent): void => {
        if (this.areWorkItemsDirty.value) {
            e.returnValue = "dirtyState";
            e.preventDefault();
        }
    };

    private registerShortcuts = (): void => {
        const { onFocusNext, onFocusPrevious, onRunQuery, onToggleFilterBar } = this.props;

        this.shortcutService.registerShortcut(Resources.QueriesHubHeader, {
            combos: ["z"],
            description: Resources.ToggleFullScreenMode,
            action: () => {
                const layoutManager = this.context.pageContext.getService<IVssLayoutManager>("IVssLayoutManager");
                layoutManager.toggleFullScreenMode();
            }
        });

        this.shortcutService.registerShortcut(Resources.QueriesHubHeader, {
            combos: ["c q"],
            description: Resources.NewQueryCommand,
            action: () => {
                this.openNewQuery();
            }
        });

        this.shortcutService.registerShortcut(Resources.QueriesHubHeader, {
            combos: ["ctrl+s"],
            description: Resources.SaveQueryCommand,
            globalCombos: ["ctrl+s"],
            action: () => {
                const queryTab = document.getElementsByClassName(this.enableNewBoardsQueriesNewDesignFF ? "query-tab-bar-ff" : "query-tab-bar");
                queryTab.length && this.props.selectedTabId === QueryTabId.Editor && this._saveQuery();
            }
        });

        if (onRunQuery) {
            this.shortcutService.registerShortcut(Resources.QueriesHubHeader, {
                combos: ["r", "alt+r"],
                description: Resources.RefreshQuery,
                globalCombos: ["alt+r"],
                action: () => {
                    onRunQuery();
                }
            });
        }

        if (this.queryService.view.value === QueryHubView.WorkItemEdit) {
            this.shortcutService.registerShortcut(Resources.QueriesHubHeader, {
                combos: ["alt+q"],
                description: Resources.BackToQuery,
                globalCombos: ["alt+q"],
                action: () => {
                    this._onReturnToQuery();
                }
            });
        }

        if (onFocusNext) {
            this.shortcutService.registerShortcut(Resources.QueriesHubHeader, {
                combos: ["j", "alt+n"],
                description: Resources.NextWorkItem,
                globalCombos: ["alt+n"],
                action: () => {
                    onFocusNext();
                }
            });
        }

        if (onFocusPrevious) {
            this.shortcutService.registerShortcut(Resources.QueriesHubHeader, {
                combos: ["k", "alt+p"],
                description: Resources.PreviousWorkItem,
                globalCombos: ["alt+p"],
                action: () => {
                    onFocusPrevious();
                }
            });
        }

        if (onToggleFilterBar) {
            this.shortcutService.registerShortcut(Resources.QueriesHubHeader, {
                combos: ["mod+shift+f"],
                description: Resources.FilterResults,
                globalCombos: ["mod+shift+f"],
                action: () => {
                    onToggleFilterBar();

                    const hotKeyEventService = this.context.pageContext.getService<IHotKeyEventService>("IHotKeyEventService");
                    hotKeyEventService.fireEvent({}, IHotKeyEventType.FilterToggled);
                }
            });
        }
    };

    private unregisterShortcuts = () => {
        this.shortcutService.removeShortcutGroup(Resources.QueriesHubHeader);
    };

    private openNewQuery = async () => {
        const queryListService = this.context.pageContext.getService<IQueryListService>("IQueryListService");

        // ensure root query data is populated
        await queryListService.getQueriesAsync();
        const url = getEditorUrl(this.context.pageContext, undefined, queryListService.rootMyQueriesFolderId);
        onClickFPS(this.context.pageContext, url, true);
    };

    private getItemsCount = (queryResultItems: IReadyableObservableArray<IQueryResultItem>): number => {
        if (this.props.isFiltering) {
            return queryResultItems.length;
        }
        return this.props.targetIdsCount ?? queryResultItems.length;
    };

    public render(): JSX.Element {
        const {
            hideFocusArrows,
            isImport,
            lastFocusedRow,
            onToggleFilterBar,
            onFocusNext,
            onFocusPrevious,
            onSplitterDirectionSelect,
            queryResultItems,
            selectedTabId,
            selection
        } = this.props;

        const isWorkItemEditor = this.queryService.view.value === QueryHubView.WorkItemEdit;
        const queryHeaderTabContainerClass = this.enableNewBoardsQueriesNewDesignFF ? "query-header-tab-container-ff" : "query-header-tab-container";
        const queryHeaderBreadcrumbContainerClass = this.enableNewBoardsQueriesNewDesignFF ? "query-header-breadcrumb-container-ff" : "query-header-breadcrumb-container";

        return (
            <>
                <Observer
                    lastFocusedRow={lastFocusedRow}
                    queryResultItems={queryResultItems}
                    queryResultItemsReady={queryResultItems?.ready}
                    isQueryRunning={this.queryService.isQueryRunning}
                    selection={selection}
                    fullScreen={this.layoutManager.getFullScreenMode()}
                >
                    {(observerProps: {
                        lastFocusedRow: number | undefined;
                        queryResultItems: IQueryResultItem[] | undefined;
                        queryResultItemsReady: boolean | undefined;
                        isQueryRunning: boolean;
                        selection: ISelectionRange[] | undefined;
                        fullScreen: boolean;
                    }) => (

                        <div className={css(queryHeaderTabContainerClass, isWorkItemEditor ? "flex-row query-header-border" : "flex-column")}>
                            <div className={css(queryHeaderBreadcrumbContainerClass, "flex-center flex-row", isWorkItemEditor && "flex-grow")}>
                                <QueryBreadcrumb query={this.queryService.query} className={!isWorkItemEditor ? "flex-grow" : ""} useNewQueryBreadcrumbDesign={this.enableNewBoardsQueriesNewDesignFF} />
                                {isWorkItemEditor && (
                                    <>
                                        <span className="header-item-divider margin-horizontal-8" />
                                        <Button
                                            ariaLabel={Resources.BackToQueryAriaLabel}
                                            tooltipProps={{ text: Resources.BackToQueryAriaLabel }}
                                            text={Resources.BackToQuery}
                                            iconProps={{ iconName: "RevToggleKey" }}
                                            onClick={this._onReturnToQuery}
                                            subtle={true}
                                        />
                                    </>
                                )}
                                { this.enableNewBoardsQueriesNewDesignFF ? this._renderTabBarCommands() : (
                                    queryResultItems && selection && (
                                        <div className="query-result-count-container flex-column flex-center">
                                            <span className="query-result-count">
                                                {observerProps.isQueryRunning
                                                    ? Resources.Querying
                                                    : !observerProps.queryResultItemsReady
                                                    ? Resources.RunToSeeResults
                                                    : queryResultItems.length > 0
                                                    ? format(Resources.WorkItemCount, this.getItemsCount(queryResultItems))
                                                    : Resources.NoQueryResults}
                                            </span>
                                            {queryResultItems.ready.value && (
                                                <span className="query-selection-count margin-left-8">{this.getLinksStatusString()}</span>
                                            )}
                                            {queryResultItems.ready.value && selection.selectedCount > 0 && (
                                                <span className="query-selection-count margin-left-8">
                                                    {format(Resources.SelectedCount, selection.selectedCount)}
                                                </span>
                                            )}
                                        </div>
                                    )
                                )}

                            </div>
                            <ScreenSizeObserver>
                                {(screenSizeProps: { screenSize: ScreenSize }) => {
                                    const smallScreen = screenSizeProps.screenSize === ScreenSize.xsmall;
                                    return (
                                    <>
                                        <div className={css(
                                            "flex-row",
                                            isWorkItemEditor && "query-tab-bar-container",
                                            this.enableNewBoardsQueriesNewDesignFF && (isImport || this.queryService.isRecycleBin) && "query-bar-container-button-groups-ff"
                                            )}
                                        >

                                            {this.enableNewBoardsQueriesNewDesignFF ? (
                                                !isImport && !this.queryService.isRecycleBin && selectedTabId ? (
                                                    <TabProvider providers={this.contributedTabs} selectedTabId={selectedTabId}>
                                                        <TabBar
                                                            selectedTabId={selectedTabId}
                                                            onSelectedTabChanged={this._onSelectedTabChanged}
                                                            disableSticky={false}
                                                            tabsClassName="query-tabs-ff"
                                                            className="query-tab-bar-ff flex-grow scroll-hidden flex-self-start"
                                                            tabSize={TabSize.Compact}
                                                        >
                                                            <Tab id={QueryTabId.Results} name={Resources.ResultsTab} />
                                                            <Tab id={QueryTabId.Editor} name={Resources.EditorTab} />
                                                            {this.hasBacklogManagementPermission && (
                                                                <Tab id={QueryTabId.Charts} name={Resources.ChartsTab} />
                                                            )}
                                                        </TabBar>
                                                    </TabProvider>
                                                ) : null
                                            ) : (isImport ? (
                                                    this.importCommandBar()
                                                ) : this.queryService.isRecycleBin ? (
                                                    this.recycleBinCommandBar()
                                                ) : selectedTabId ? (
                                                    <TabProvider providers={this.contributedTabs} selectedTabId={selectedTabId}>
                                                        <TabBar
                                                            selectedTabId={selectedTabId}
                                                            onSelectedTabChanged={this._onSelectedTabChanged}
                                                            renderAdditionalContent={this._renderTabBarCommands}
                                                            disableSticky={false}
                                                            tabsClassName="query-tabs"
                                                            className="query-tab-bar flex-grow scroll-hidden"
                                                        >
                                                            <Tab id={QueryTabId.Results} name={Resources.ResultsTab} />
                                                            <Tab id={QueryTabId.Editor} name={Resources.EditorTab} />
                                                            {this.hasBacklogManagementPermission && (
                                                                <Tab id={QueryTabId.Charts} name={Resources.ChartsTab} />
                                                            )}
                                                        </TabBar>
                                                    </TabProvider>
                                                ) : null
                                            )}

                                            {queryResultItems &&
                                                queryResultItems.length > 0 &&
                                                lastFocusedRow &&
                                                lastFocusedRow.value >= 0 &&
                                                !smallScreen && (
                                                    <div className="flex-row flex-center margin-right-8">
                                                        {format(
                                                            Resources.SelectedWorkItem,
                                                            (observerProps.lastFocusedRow as number) + 1,
                                                            this.getItemsCount(queryResultItems)
                                                        )}
                                                    </div>
                                                )}
                                            <ButtonGroup>
                                                {onFocusPrevious && !smallScreen && !hideFocusArrows && (
                                                    <Button
                                                        ariaLabel={Resources.PreviousWorkItem}
                                                        subtle={true}
                                                        iconProps={{ iconName: "Up" }}
                                                        onClick={onFocusPrevious}
                                                        disabled={(observerProps.lastFocusedRow as number) <= 0}
                                                        tooltipProps={{ text: Resources.PreviousWorkItem }}
                                                    />
                                                )}
                                                {onFocusNext && !smallScreen && !hideFocusArrows && (
                                                    <Button
                                                        ariaLabel={Resources.NextWorkItem}
                                                        subtle={true}
                                                        iconProps={{ iconName: "Down" }}
                                                        onClick={onFocusNext}
                                                        disabled={
                                                            (observerProps.lastFocusedRow as number) < 0 ||
                                                            (queryResultItems &&
                                                                (observerProps.lastFocusedRow as number) === queryResultItems.length - 1)
                                                        }
                                                        tooltipProps={{ text: Resources.NextWorkItem }}
                                                    />
                                                )}
                                                {onToggleFilterBar && (
                                                    <Button
                                                        ariaLabel={Resources.FilterWorkItems}
                                                        subtle={true}
                                                        iconProps={{ iconName: "Filter" }}
                                                        onClick={onToggleFilterBar}
                                                        tooltipProps={{ text: Resources.FilterWorkItems }}
                                                    />
                                                )}
                                                {
                                                    this.enableNewBoardsQueriesNewDesignFF ? (
                                                        onSplitterDirectionSelect && queryResultItems
                                                            && queryResultItems.length > 0
                                                            && !smallScreen && screenSizeProps.screenSize !== ScreenSize.small && (
                                                            <Dropdown
                                                                ariaLabel={Resources.SplitterDirection}
                                                                items={[
                                                                    { id: SplitterOptions.Bottom, text: Resources.SplitterOptionBottom },
                                                                    { id: SplitterOptions.Right, text: Resources.SplitterOptionRight },
                                                                    { id: SplitterOptions.Off, text: Resources.SplitterOptionOff }
                                                                ]}
                                                                renderExpandable={props => (
                                                                    <DropdownExpandableButton
                                                                        {...props}
                                                                        renderSelectedItems={(selection: IListSelection, items: IListBoxItem[]) => {
                                                                            const item = items[selection.value[0].beginIndex];

                                                                            return (
                                                                                <>
                                                                                    <span className="secondary-text">{Resources.Preview}</span>
                                                                                    {" " + item.text}
                                                                                </>
                                                                            )
                                                                        }}
                                                                    />
                                                                )}
                                                                onSelect={this._onSplitterDirectionSelect}
                                                                selection={this.splitterDirectionSelection}
                                                            />
                                                        )
                                                    ) : (
                                                        onSplitterDirectionSelect && (
                                                            <Dropdown
                                                                ariaLabel={Resources.SplitterDirection}
                                                                items={[
                                                                    { id: SplitterOptions.Bottom, text: Resources.SplitterOptionBottom },
                                                                    { id: SplitterOptions.Right, text: Resources.SplitterOptionRight },
                                                                    { id: SplitterOptions.Off, text: Resources.SplitterOptionOff }
                                                                ]}
                                                                renderExpandable={props => <DropdownExpandableButton {...props} />}
                                                                onSelect={this._onSplitterDirectionSelect}
                                                                selection={this.splitterDirectionSelection}
                                                            />
                                                        )
                                                    )
                                                }
                                                <Button
                                                    ariaLabel={observerProps.fullScreen ? Resources.ExitFullScreen : Resources.EnterFullScreen}
                                                    subtle={true}
                                                    iconProps={{ iconName: observerProps.fullScreen ? "BackToWindow" : "FullScreen" }}
                                                    onClick={this._onToggleFullScreen}
                                                    tooltipProps={{
                                                        text: observerProps.fullScreen ? Resources.ExitFullScreen : Resources.EnterFullScreen
                                                    }}
                                                />
                                            </ButtonGroup>
                                        </div>
                                        </> );
                                }}
                            </ScreenSizeObserver>
                        </div>
                    )}
                </Observer>
            </>
        );
    }

    private _onReturnToQuery = () => {
        if (this.queryService.previousView === QueryHubView.QueryEdit) {
            this.queryService.changeView(QueryHubView.QueryEdit);
        } else {
            // default to the results view
            this.queryService.changeView(QueryHubView.QueryResults);
        }
    };

    private _getContributedItemContext = async () => {
        this.actionContext.query = await QueryUtils.getQueryContext(this.queryService, this.projectId);
        this.actionContext.queryText = this.queryService.wiql.value;
        this._onSelectionUpdated();
    };

    private _onSelectionUpdated = () => {
        if (this.props.selection && this.props.queryResultItems) {
            const selectedItems = QueryUtils.getSelectedItems(this.props.selection, this.props.queryResultItems.value);
            this.actionContext.workItemIds = selectedItems.map(item => item.value[CoreFieldRefNames.Id]);
            this.selectedWorkItemIds.value = this.actionContext.workItemIds;
            if (this.queryService.isRecycleBin) {
                this._permanentlyDeleteCommand.disabled = false;
                this._restoreCommand.disabled = false;
            }
        } else if (!this.props.selection && this.queryService.isRecycleBin) {
            this._permanentlyDeleteCommand.disabled = true;
            this._restoreCommand.disabled = true;
        }
    };

    private _openDialog(callout: (onDismiss: () => void) => JSX.Element): void {
        const layoutManager = this.context.pageContext.getService<IVssLayoutManager>("IVssLayoutManager");
        layoutManager.renderCallout(onDismiss => callout(onDismiss));
    }

    private _openSaveDialog = async () => {
        const historyService = this.context.pageContext.getService<IVssHistoryService>("IVssHistoryService");
        const queryListService = this.context.pageContext.getService<IQueryListService>("IQueryListService");
        if (!queryListService.queryTree || queryListService.queryTree.length === 0) {
            await queryListService.getQueriesAsync();
        }
        const routeValues = historyService.getState();
        let path = "";
        if (routeValues["parentId"]) {
            const query = await queryListService.getQueryByPath(routeValues["parentId"]);
            path = query?.path ?? "";
        }
        this._openDialog(onDismiss => {
            const mode = this.saveDialogMode.value;
            const wiql = this.queryService.wiql.value;
            return (
                <QuerySaveDialog
                    mode={this.saveDialogMode.value}
                    queryItem={
                        mode === QuerySaveDialogMode.NewQuery
                            ? ({ isFolder: false, wiql: wiql, path: path } as QueryItem)
                            : mode === QuerySaveDialogMode.RenameQuery
                            ? ({ ...this.queryService.query.value } as QueryItem)
                            : ({ ...this.queryService.query.value, wiql: wiql } as QueryItem)
                    }
                    onDismiss={onDismiss}
                    navigateToQueryOnSave={true}
                    onRename={this._onRename}
                />
            );
        });
    };

    private _renameQuery = async () => {
        await this.queryService.getQuery(this.projectId);
        this.saveDialogMode.value = QuerySaveDialogMode.RenameQuery;
        this._openSaveDialog();
    };

    private _onRename = (query: QueryItem) => {
        this.queryService.query.value = query;
    };

    private _saveQuery = async () => {
        const { isEditorDirty, isRecycleBin } = this.queryService;

        if (isRecycleBin) {
            const wiql = await this.queryService.generateWiql(this.projectId);
            const contributionService = this.context.pageContext.getService<IVssContributionService>("IVssContributionService");
            const result = await contributionService.getDataAsync<IDataProviderResponse>("ms.vss-work-web.update-ad-hoc-query-data-provider", {
                QueryId: this.queryService.queryId,
                Wiql: wiql
            });
            if (result?.errorMessage) {
                reportError(this.context.pageContext, "save-recycle-bin-query", result.errorMessage);
            }
            isEditorDirty.value = false;
            return;
        }

        const query = await this.queryService.getQuery(this.projectId);
        if (query) {
            try {
                await this.queryService.saveQuery(this.projectId);
                if (this.queryService.wiql.value.length !== 0) {
                    isEditorDirty.value = false;
                }
            } catch (error) {
                reportError(this.context.pageContext, "save-query", error.message);
            }
        } else {
            this.saveDialogMode.value = QuerySaveDialogMode.NewQuery;
            await this.queryService.generateWiql(this.projectId);
            this._openSaveDialog();
        }
    };

    private _saveAsQuery = async () => {
        if (this.queryService.isEditorDirty.value) {
            await this.queryService.generateWiql(this.projectId);
        }

        this.saveDialogMode.value = QuerySaveDialogMode.SaveAs;
        this._openSaveDialog();
    };

    private _saveWorkItems = () => {
        this._openDialog(onDismiss => <SaveWorkItemsDialog results={this.workItemResults} onDismiss={onDismiss} />);
        const dirtyItems = this.queryService.workItems.value.filter(item => item.dirty.value);
        const savingService = this.context.pageContext.getService<IWorkItemSavingService>("IWorkItemSavingService");
        savingService.beginSaveWorkItemsBatch(dirtyItems ?? [], this._saveSuccessCallback, this._saveErrorCallback);
    };

    private _saveSuccessCallback = (result: IWorkItemsBulkSaveSuccessResult | IWorkItemBulkSaveResult[]) => {
        if (this._isWorkItemBulkSaveSuccessResult(result)) {
            this.workItemResults.value = result.workItems.map(item => {
                return { workItem: item };
            });
            this.props.onSaveItemsComplete?.(result.workItems);
        } else {
            this.workItemResults.value = result as IWorkItemBulkSaveResult[];
        }

        this.handleImport(this.workItemResults.value.map(v => v.workItem));
    };

    private _saveErrorCallback = (result: IWorkItemSavingFailedError) => {
        this.workItemResults.value = result.results || [];
        this.handleImport(this.workItemResults.value.filter(result => !result.error).map(v => v.workItem));
    };

    private getNewMenuItems(): IReadonlyObservableArray<IMenuItem> {
        // this.projectId is not yet defined because this will run before the constructor
        // but fall back to it if getTFSPageData.project gives us undefined
        const projectId = getTFSPageData(this.context.pageContext)?.project?.id ?? this.projectId;
        const newMenuItems: IObservableArray<IMenuItem> = new ObservableArray([
            {
                id: "new-query",
                text: Resources.NewQueryCommand,
                iconProps: {
                    iconName: "Add"
                },
                groupKey: "query",
                onActivate: () => {
                    this.openNewQuery();
                }
            }
        ]);

        const renderMenuCell = (menuCell: MenuCell, menuItem: IMenuItem, details: IMenuItemDetails) => {
            if (menuCell === MenuCell.Icon) {
                return (
                    this.props.workItemTrackingDataProvider && (
                        <WorkItemTypeIconProvider
                            dataProvider={this.props.workItemTrackingDataProvider}
                            dataSource={{
                                projectIdOrName: this.projectId,
                                workItemTypeName: menuItem.text ?? ""
                            }}
                        >
                            {icon => <WorkItemTypeIcon data={icon} className="icon-margin font-size-m" />}
                        </WorkItemTypeIconProvider>
                    )
                );
            }
            return undefined;
        };

        const getWorkItemTypesItems = async () => {
            const typesService = this.context.pageContext.getService<IWorkItemTypesService>("IWorkItemTypesService");
            const types = await typesService.getVisibleWorkItemTypes(projectId);
            const items: IMenuItem[] = [];

            for (const workItemType of types) {
                items.push({
                    id: workItemType,
                    text: workItemType,
                    groupKey: "types",
                    renderMenuCell: renderMenuCell,
                    onActivate: () => {
                        const layoutManager = this.context.pageContext.getService<IVssLayoutManager>("IVssLayoutManager");
                        layoutManager.renderCallout(onDismiss => {
                            return <WorkItemFormDialog workItemTypeName={workItemType} onDismiss={onDismiss} />;
                        });
                    }
                });
            }

            newMenuItems.push(...items);
        };

        getWorkItemTypesItems();
        return newMenuItems;
    }

    private handleImport = (workItems: IWorkItem[]): void => {
        if (!this.props.isImport) {
            return;
        }

        // clean up links created between imported work items
        this.cleanupNewWorkItemLinks(workItems);
    };

    private cleanupNewWorkItemLinks = (workItems: IWorkItem[]): void => {
        for (const workItem of workItems) {
            const newWorkItemLinks = workItem.getLinks().filter(link => link?.linkData?.ID && link.linkData.ID < 0);
            workItem.removeLinks(newWorkItemLinks);
        }
    };

    private _isWorkItemBulkSaveSuccessResult(
        result: IWorkItemsBulkSaveSuccessResult | IWorkItemBulkSaveResult[]
    ): result is IWorkItemsBulkSaveSuccessResult {
        return "workItems" in result;
    }

    private _onImportCSVClick = () => {
        if (this.props.onOpenImportPanel) {
            this.props.onOpenImportPanel();
        }
    };
    private _onExportToCSV = () => {
        const projectId = getTFSPageData(this.context.pageContext)?.project?.id ?? "";
        const csvExporter = new QueryResultCSVExporter(this.context.pageContext, projectId, this.props.queryResultItems?.value);
        csvExporter.downloadCSV();
    };
    private _onSplitterDirectionSelect = (event: React.SyntheticEvent<HTMLElement, Event>, item: IListBoxItem) => {
        if (this.props.onSplitterDirectionSelect) {
            this.props.onSplitterDirectionSelect(item.id);
        }
    };

    private _onToggleFullScreen = () => {
        this.layoutManager.setFullScreenMode(!this.layoutManager.getFullScreenMode().value);
    };

    private _onSelectedTabChanged = (selectedTabId: string) => {
        if (this.props.selectedTabId === selectedTabId) {
            return;
        }

        if (selectedTabId === QueryTabId.Editor) {
            this.queryService.changeView(QueryHubView.QueryEdit);
        } else if (selectedTabId === QueryTabId.Results) {
            this.queryService.changeView(QueryHubView.QueryResults);
        } else if (selectedTabId === QueryTabId.Charts) {
            this.queryService.changeView(QueryHubView.QueryCharts);
        } else {
            // presumably a contributed tab
            this.queryService.changeView(selectedTabId);
        }
    };

    private _renderTabBarCommands = () => {
        let commandBarItems: IHeaderCommandBarItem[];

        if (this.props.isImport && this.enableNewBoardsQueriesNewDesignFF) {
            return this.importCommandBar();
        } else if (this.queryService.isRecycleBin && this.enableNewBoardsQueriesNewDesignFF) {
            return this.recycleBinCommandBar();
        }

       else if (this.props.selectedTabId === QueryTabId.Charts) {
            commandBarItems = this._chartsTabBarCommands;
            commandBarItems.forEach(
                cbi =>
                    (cbi.disabled =
                        this.queryService.isNewQuery ||
                        !this.props.queryType ||
                        this.props.queryType !== QueryType.Flat ||
                        this.queryService.isEditorDirty.value)
            );
        } else if (this.props.selectedTabId === QueryTabId.Editor) {
            commandBarItems = this._defaultCommandBarItems.slice();
            // add additional options for the editor tab
            if (!this.queryService.isNewQuery) {
                commandBarItems.push(this._saveAsCommandBarItem);
                commandBarItems.push(this._renameCommand);
            }
            commandBarItems.push(this._revertChangesCommandBarItem);
        } else if (this.props.selectedTabId === QueryTabId.Results) {
            commandBarItems = this._defaultCommandBarItems.slice();
            if (!this.queryService.isNewQuery) {
                commandBarItems.push(this._renameCommand);
            }
        } else {
            return;
        }

        return this.props.selectedTabId !== QueryTabId.Editor ? (
            <Observer contributedActions={this.contributedActions} isDirty={this.props.isDirty} areWorkItemsDirty={this.areWorkItemsDirty}>
                {(observableProps: { contributedActions: IMenuItem[]; isDirty: boolean | undefined; areWorkItemsDirty: boolean }) => {
                    const saveButtonIndex = commandBarItems.findIndex(i => i.id === "save-query");
                    if (saveButtonIndex !== -1) {
                        commandBarItems[saveButtonIndex].disabled = !observableProps.isDirty;
                    }
                    const saveItems = commandBarItems.find(i => i.id === "save-items");
                    if (saveItems) {
                        saveItems.disabled = !observableProps.areWorkItemsDirty; // Save Work Items Button
                    }
                    return (
                        <HeaderCommandBar
                            className={this.enableNewBoardsQueriesNewDesignFF ? "query-command-bar-ff flex-grow scroll-hidden" : "query-command-bar flex-grow scroll-hidden"}
                            reduceFlickering={this.responsiveLayoutFlickeringFixEnabled}
                            items={[...commandBarItems, ...observableProps.contributedActions]}
                            buttonCount={commandBarItems.length}
                        />
                    );
                }}
            </Observer>
        ) : (
            <Observer contributedActions={this.contributedActions} isDirty={this.props.isDirty} areWorkItemsDirty={this.areWorkItemsDirty}>
                {(observableProps: { contributedActions: IMenuItem[]; isDirty: boolean | undefined; areWorkItemsDirty: boolean }) => {
                    const saveItemsIndex = commandBarItems.findIndex(i => i.id === "save-items");
                    if (saveItemsIndex !== -1) {
                        commandBarItems[saveItemsIndex].disabled = !observableProps.areWorkItemsDirty;
                    }

                    if (this.queryService.queryId && !(this.queryService.tempQueryId || this.queryService.urlWiql)) {
                        const saveButtonIndex = commandBarItems.findIndex(i => i.id === "save-query");
                        const revertButtonIndex = commandBarItems.findIndex(i => i.id === "revert-changes");
                        commandBarItems[saveButtonIndex].disabled = !observableProps.isDirty; // Save Button
                        commandBarItems[revertButtonIndex].disabled = !observableProps.isDirty; // Revert Changes Button
                    } else {
                        commandBarItems = commandBarItems.filter(i => i.id !== "save-as" && i.id !== "rename-query");
                        const saveButtonIndex = commandBarItems.findIndex(i => i.id === "save-query");
                        if (saveButtonIndex !== -1) {
                            commandBarItems[saveButtonIndex].disabled = false;
                        }
                        this._revertChangesCommandBarItem.disabled = !observableProps.isDirty;
                    }
                    return (
                        <HeaderCommandBar
                            className={this.enableNewBoardsQueriesNewDesignFF ? "query-command-bar-ff flex-grow scroll-hidden" : "query-command-bar flex-grow scroll-hidden"}
                            reduceFlickering={this.responsiveLayoutFlickeringFixEnabled}
                            items={[...commandBarItems, ...observableProps.contributedActions]}
                            buttonCount={commandBarItems.length}
                        />
                    );
                }}
            </Observer>
        );
    };

    private subscribeToWorkItemChanges() {
        const items = this.queryService.workItems;
        items.subscribe(this.onWorkItemListChanged);
        items.value.forEach(workItem => {
            workItem.dirty.subscribe(this.updateSaveButton);
        });
        this.updateSaveButton();
    }

    private onWorkItemListChanged = (value: IObservableArrayEventArgs<IWorkItem>, action: ObservableArrayAction): void => {
        value.addedItems?.forEach(workItem => {
            workItem.dirty.subscribe(this.updateSaveButton);
        });

        value.removedItems?.forEach(workItem => {
            workItem.dirty.unsubscribe(this.updateSaveButton);
        });

        this.updateSaveButton();
    };

    private unsubscribeFromWorkItemChanges = (): void => {
        const items = this.queryService.workItems;
        items.unsubscribe(this.subscribeToWorkItemChanges);
        items.value.forEach(workItem => workItem.dirty.unsubscribe(this.updateSaveButton));
    };

    private updateSaveButton = () => {
        // if any work item is dirty, enable the save button
        const dirty = !!this.queryService.workItems.value.find(workItem => workItem.dirty.value);
        if (this.areWorkItemsDirty.value !== dirty) {
            this.areWorkItemsDirty.value = dirty;
        }
    };

    private importCommandBar = () => {
        return (
            <Observer areWorkItemsDirty={this.areWorkItemsDirty} queryItems={this.props.queryResultItems}>
                {(observableProps: {
                    areWorkItemsDirty: boolean;
                    queryItems: IQueryResultItem[] | undefined;
                }) => {
                    // save all
                    this._importSaveCommand.disabled =
                        !observableProps.queryItems?.length || !observableProps.areWorkItemsDirty;
                    // export to csv
                    this._importExportCommand.disabled = !observableProps.queryItems?.length;
                    return (
                        <HeaderCommandBar
                            className= {this.enableNewBoardsQueriesNewDesignFF ? "query-command-bar-ff flex-grow scroll-hidden" : "query-command-bar flex-grow scroll-hidden"}
                            items={this._importHeaderCommands}
                        />
                    );
                }}
            </Observer>
        );
    }

    private recycleBinCommandBar = () => {
        return (
            <Observer
            isEditorDirty={this.queryService.isEditorDirty}
            selectedItems={this.props.selection}
            permissions={this.workItemPermissions}
            >
                {(observableProps: {
                    isEditorDirty: boolean;
                    permissions: IWorkItemPermissionData | undefined;
                }) => {
                    let commandBarItems = this._recycleBinHeaderCommands;
                    if (this.queryService.isRecycleBin && !this.queryService.canViewRecycleBin) {
                        commandBarItems = [];
                    }

                    const areItemsAreAvailable =
                        this.props.queryResultItems &&
                        this.props.queryResultItems.length > 0 &&
                        this.props.selection &&
                        this.props.selection.selectedCount > 0;
                    this._permanentlyDeleteCommand.disabled = this._restoreCommand.disabled = !areItemsAreAvailable;

                    const canDeleteWorkItems =
                        observableProps.permissions?.workItemDeletePermission?.hasPermission;

                    const canDestroyWorkItems =
                        observableProps.permissions?.workItemDestroyPermission?.hasPermission;

                    if (!canDeleteWorkItems) {
                        commandBarItems = commandBarItems.filter(item => item.id !== "restore-item");
                    }

                    if (!canDestroyWorkItems) {
                        commandBarItems = commandBarItems.filter(item => item.id !== "permanently-delete-item");
                    }
                    this._saveQueryCommand.disabled = !observableProps.isEditorDirty;
                    return (
                        <HeaderCommandBar
                            buttonCount={5}
                            className= {this.enableNewBoardsQueriesNewDesignFF ? "query-command-bar-ff flex-grow scroll-hidden" : "query-command-bar flex-grow scroll-hidden"}
                            items={commandBarItems}
                        />
                    );
                }}
            </Observer>
        );
    }

    private _saveAsCommandBarItem = {
        id: "save-as",
        text: Resources.SaveAs,
        iconProps: {
            iconName: "SaveAs"
        },
        subtle: true,
        onActivate: () => {
            this._saveAsQuery();
        },
        important: isFeatureFlagEnabled(this.context.pageContext, FeatureFlags.EnableNewBoardsQueriesNewDesign) ? false : undefined,
        rank: 35
    };

    private _revertChangesCommandBarItem = {
        id: "revert-changes",
        text: Resources.RevertChanges,
        iconProps: {
            iconName: "Undo"
        },
        subtle: true,
        disabled: this.props.isDirty !== undefined && !this.props.isDirty.value,
        tooltipProps: { text: Resources.RevertChangesTooltip },
        onActivate: () => {
            this._openDialog(onDismiss => (
                <RevertDialog
                    pageContext={this.context.pageContext}
                    query={this.queryService.query.value}
                    onDismiss={onDismiss}
                    onRevert={this.props.onRevert}
                />
            ));
        },
        important: isFeatureFlagEnabled(this.context.pageContext, FeatureFlags.EnableNewBoardsQueriesNewDesign) ? false : undefined,
        rank: 45
    };

    private readonly _saveQueryCommand = {
        id: "save-query",
        text: isFeatureFlagEnabled(this.context.pageContext, FeatureFlags.EnableNewBoardsQueriesNewDesign) ? Resources.SaveCommand : Resources.SaveQueryCommand,
        iconProps: {
            iconName: "Save"
        },
        subtle: !isFeatureFlagEnabled(this.context.pageContext, FeatureFlags.EnableNewBoardsQueriesNewDesign, false),
        tooltipProps: { text: Resources.SaveQueryCommandTooltip },
        disabled: this.props.isDirty !== undefined && !this.props.isDirty.value,
        onActivate: () => {
            this._saveQuery();
        },
        rank: 30
    };

    private readonly _newCommand = {
        id: "new-option",
        text: Resources.NewCommand,
        iconProps: {
            iconName: "Add"
        },
        subtle: !isFeatureFlagEnabled(this.context.pageContext, FeatureFlags.EnableNewBoardsQueriesNewDesign, false),
        subMenuProps: {
            id: "new-items",
            items: this.getNewMenuItems()
        },
        rank: 20
    };

    private readonly _runQueryCommand =  {
        id: "run-query",
        text: Resources.RunQueryCommand,
        iconProps: {
            iconName: "Play"
        },
        subtle: isFeatureFlagEnabled(this.context.pageContext, FeatureFlags.EnableNewBoardsQueriesNewDesign, false) ? undefined : true,
        tooltipProps: { text: Resources.RunQueryCommandTooltip },
        onActivate: this.props.onRunQuery,
        isPrimary: isFeatureFlagEnabled(this.context.pageContext, FeatureFlags.EnableNewBoardsQueriesNewDesign) ? true : undefined,
        rank: 10
    };

    private readonly _defaultCommandBarItems: IHeaderCommandBarItem[] = [
        this._runQueryCommand,
        this._newCommand,
        this._saveQueryCommand,
        {
            id: "column-options",
            text: Resources.ColumnOptions,
            iconProps: {
                iconName: "Repair"
            },
            subtle: true,
            onActivate: () => {
                this._openDialog(onDismiss => <ColumnOptionsPanelContainer onDismiss={onDismiss} />);
            },
            rank: isFeatureFlagEnabled(this.context.pageContext, FeatureFlags.EnableNewBoardsQueriesNewDesign) ? 40 : 50
        },
        {
            id: "save-items",
            text: Resources.SaveItemsCommand,
            iconProps: {
                iconName: "SaveAll"
            },
            onActivate: this._saveWorkItems,
            subtle: true,
            rank: isFeatureFlagEnabled(this.context.pageContext, FeatureFlags.EnableNewBoardsQueriesNewDesign) ? 50 : 60
        },
        {
            id: "email-query",
            text: Resources.EmailQueryCommand,
            iconProps: {
                iconName: "Mail"
            },
            subtle: true,
            onActivate: () => {
                const referenceFieldNames = this.queryService.queryResult.value?.data?.columns.map(column => column.name).filter(v => v) as string[];
                const columnFieldNames = this.queryService.queryResult.value?.data?.columns.map(column => column.text).filter(v => v) as string[];
                this._openDialog(onDismiss => (
                    <Observer queryResultItems={this.props.queryResultItems} loaded={this.props.queryResultItems?.ready}>
                        {({ queryResultItems, loaded }: { queryResultItems: undefined | IQueryResultItem[]; loaded: boolean | undefined }) => (
                            <EmailWorkItemsDialog
                                columnFieldNames={columnFieldNames || []}
                                referenceFieldNames={referenceFieldNames || []}
                                defaultSubject={this.queryService.query.value && this.queryService.query.value.name}
                                onDismiss={onDismiss}
                                selectedItems={queryResultItems?.map(item => item.value) || []}
                                loading={!loaded}
                                isQueryDirty={this.queryService.isEditorDirty.value || this.queryService.isNewQuery}
                                queryId={this.queryService.query?.value?.id}
                                tempQueryId={this.queryService.tempQueryId}
                                wiql={this.queryService.wiql.value}
                            />
                        )}
                    </Observer>
                ));
            },
            rank: 70
        },
        {
            id: "copy-url",
            text: Resources.CopyQueryUrlCommand,
            iconProps: {
                iconName: "Copy"
            },
            subtle: true,
            onActivate: () => {
                this._openDialog(onDismiss => (
                    <CopyUrlDialog
                        onDismiss={onDismiss}
                        pageContext={this.context.pageContext}
                        wiql={this.queryService.wiql.value}
                        queryType={QueryType.Flat}
                    />
                ));
            },
            rank: 80
        },
        {
            id: "export-csv",
            text: Resources.ExportToCsvCommand,
            iconProps: {
                iconName: "Download"
            },
            subtle: true,
            rank: 90,
            onActivate: () => {
                this._onExportToCSV();
            }
        }
    ];

    private readonly _chartsTabBarCommands = [
        {
            id: "refresh-charts",
            text: Resources.RefreshCharts,
            iconProps: {
                iconName: "Refresh"
            },
            onActivate: this.props.onRunQuery,
            rank: 10
        },
        {
            id: "new-chart",
            text: Resources.NewChart,
            iconProps: {
                iconName: "Add"
            },
            onActivate: this.props.onAddChart,
            rank: 20
        }
    ];

    private readonly _renameCommand = {
        id: "rename-query",
        text: Resources.RenameCommand,
        iconProps: {
            iconName: "Rename"
        },
        subtle: true,
        onActivate: () => {
            this._renameQuery();
        },
        rank: isFeatureFlagEnabled(this.context.pageContext, FeatureFlags.EnableNewBoardsQueriesNewDesign) ? 60 : 40
    };

    private readonly _importSaveCommand = {
        id: "save-items",
        text: Resources.SaveItemsCommand,
        iconProps: {
            iconName: "SaveAll"
        },
        subtle: true,
        disabled: !this.props.queryResultItems?.value?.length,
        onActivate: this._saveWorkItems,
        rank: 10
    };

    private readonly _importExportCommand = {
        id: "export-csv",
        text: Resources.ExportToCsvCommand,
        iconProps: {
            iconName: "Download"
        },
        subtle: true,
        disabled: !this.props.queryResultItems?.value?.length,
        rank: 30,
        onActivate: () => {
            this._onExportToCSV();
        }
    };

    private readonly _importHeaderCommands = [
        this._importSaveCommand,
        {
            id: "import-csv",
            text: Resources.ImportWorkItems,
            iconProps: {
                iconName: "Upload"
            },
            subtle: true,
            onActivate: this._onImportCSVClick,
            rank: 20
        },
        this._importExportCommand
    ];

    private readonly _restoreCommand = WorkItemUtils.getRestoreCommandMenuItem(
        this.context.pageContext,
        this.selectedWorkItemIds,
        this.props.onRunQuery,
        false,
        /** rank */ 30
    );
    private readonly _permanentlyDeleteCommand = WorkItemUtils.getPermanentlyDeleteCommandMenuItem(
        this.context.pageContext,
        this.projectId,
        this.selectedWorkItemIds,
        this.props.onRunQuery,
        false,
        /** rank */ 40
    );

    private readonly _recycleBinHeaderCommands = [
        this._runQueryCommand,
        this._newCommand,
        this._saveQueryCommand,
        this._restoreCommand,
        this._permanentlyDeleteCommand,
        {
            id: "column-options",
            text: Resources.ColumnOptions,
            iconProps: {
                iconName: "Repair"
            },
            subtle: true,
            onActivate: () => {
                this._openDialog(onDismiss => <ColumnOptionsPanelContainer onDismiss={onDismiss} />);
            },
            rank: 50
        }
    ];
}
